<script>
  /**
   * Informes.
   *
   * ── POR QUÉ BARRAS Y NO TORTA ───────────────────────────────────────────────
   *
   * Una torta obliga a comparar ángulos, que el ojo hace mal. Unas barras
   * ordenadas de mayor a menor se leen de un vistazo y además caben las
   * etiquetas, que en una torta terminan en una leyenda aparte que toca
   * cruzar con los colores.
   *
   * Todo se dibuja con SVG a mano. Una librería de gráficas pesa más que
   * toda esta app junta, y aquí no hace falta.
   */
  import { formatear, formatearCorto, formatearAria } from '../lib/dinero.js';
  import { NATURALEZA } from '../lib/db.js';

  let {
    abierta = $bindable(false),
    movimientos = [],
    categorias = [],
    cuentas = [],
  } = $props();

  let mesElegido = $state(mesActual());

  function mesActual() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  function nombreMes(periodo) {
    const [a, m] = periodo.split('-');
    const texto = new Date(Number(a), Number(m) - 1, 1)
      .toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
    // Se pone la mayúscula aquí y no con `text-transform: capitalize`, que
    // capitaliza CADA palabra y deja "Septiembre De 2026".
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }

  const categoriasPorId = $derived(Object.fromEntries(categorias.map((c) => [c.id, c])));

  /** Los movimientos vivos, sin las filas hijas del 4x1000 mezcladas. */
  const vivos = $derived(movimientos.filter((m) => !m.borrado_en));

  /** Todos los meses que tienen algo, del más nuevo al más viejo. */
  const meses = $derived.by(() => {
    const s = new Set(vivos.map((m) => m.fecha?.slice(0, 7)).filter(Boolean));
    return [...s].sort().reverse();
  });

  const delMes = $derived(vivos.filter((m) => m.fecha?.startsWith(mesElegido)));

  const ingresos = $derived(
    delMes.filter((m) => m.tipo === 'ingreso').reduce((t, m) => t + m.monto, 0)
  );
  const egresos = $derived(
    delMes.filter((m) => m.tipo === 'egreso').reduce((t, m) => t + m.monto, 0)
  );
  const balance = $derived(ingresos - egresos);

  /**
   * Tasa de ahorro: qué parte de lo que entró NO se gastó.
   * Es el indicador que más dice de una situación financiera, y el que
   * ninguna app muestra en primera plana.
   */
  const tasaAhorro = $derived(ingresos > 0 ? (balance / ingresos) * 100 : null);

  /** Gastos agrupados por categoría, de mayor a menor. */
  const porCategoria = $derived.by(() => {
    const mapa = new Map();
    for (const m of delMes) {
      if (m.tipo !== 'egreso') continue;
      const cat = categoriasPorId[m.categoria_id];
      const nombre = cat?.nombre ?? (m.concepto === '4x1000' ? '4x1000' : 'Sin categoría');
      mapa.set(nombre, (mapa.get(nombre) ?? 0) + m.monto);
    }
    return [...mapa.entries()]
      .map(([nombre, monto]) => ({ nombre, monto }))
      .sort((a, b) => b.monto - a.monto);
  });

  const mayorCategoria = $derived(porCategoria[0]?.monto ?? 0);

  /** Comparación de los últimos seis meses. */
  const ultimosMeses = $derived.by(() => {
    return meses.slice(0, 6).reverse().map((periodo) => {
      const delPeriodo = vivos.filter((m) => m.fecha?.startsWith(periodo));
      return {
        periodo,
        etiqueta: periodo.slice(5) + '/' + periodo.slice(2, 4),
        ingresos: delPeriodo.filter((m) => m.tipo === 'ingreso').reduce((t, m) => t + m.monto, 0),
        egresos: delPeriodo.filter((m) => m.tipo === 'egreso').reduce((t, m) => t + m.monto, 0),
      };
    });
  });

  const topeMeses = $derived(
    Math.max(1, ...ultimosMeses.flatMap((m) => [m.ingresos, m.egresos]))
  );

  /** Regla 50/30/20: necesidades, gustos y ahorro o deuda. */
  const cincuenta = $derived.by(() => {
    const grupos = { necesidad: 0, deseo: 0, ahorro_deuda: 0, sin: 0 };
    for (const m of delMes) {
      if (m.tipo !== 'egreso') continue;
      const cat = categoriasPorId[m.categoria_id];
      const n = cat?.naturaleza;
      if (n === NATURALEZA.NECESIDAD) grupos.necesidad += m.monto;
      else if (n === NATURALEZA.DESEO) grupos.deseo += m.monto;
      else if (n === NATURALEZA.AHORRO_DEUDA) grupos.ahorro_deuda += m.monto;
      else grupos.sin += m.monto;
    }
    const total = grupos.necesidad + grupos.deseo + grupos.ahorro_deuda + grupos.sin;
    // Los porcentajes se calculan aquí y no en el marcado: Svelte solo
    // permite {@const} como hijo directo de un bloque, y meterlo dentro de
    // un <section> rompe la compilación.
    const pct = (v) => (total ? (v / total) * 100 : 0);
    return {
      ...grupos,
      total,
      pctNecesidad: pct(grupos.necesidad),
      pctDeseo: pct(grupos.deseo),
      pctDeuda: pct(grupos.ahorro_deuda),
      pctSin: pct(grupos.sin),
    };
  });

  /** Lo que se ha ido en 4x1000 en todo el año. Suele doler verlo. */
  const gmfDelAnio = $derived.by(() => {
    const anio = mesElegido.slice(0, 4);
    return vivos
      .filter((m) => m.fecha?.startsWith(anio) && m.concepto === '4x1000')
      .reduce((t, m) => t + m.monto, 0);
  });

  /** Dónde más gastas. */
  const topComercios = $derived.by(() => {
    const mapa = new Map();
    for (const m of delMes) {
      if (m.tipo !== 'egreso') continue;
      const donde = (m.contraparte || '').trim();
      if (!donde) continue;
      mapa.set(donde, (mapa.get(donde) ?? 0) + m.monto);
    }
    return [...mapa.entries()]
      .map(([nombre, monto]) => ({ nombre, monto }))
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 5);
  });

  const COLORES = ['var(--cat-1)', 'var(--cat-2)', 'var(--cat-3)', 'var(--cat-4)',
                   'var(--cat-5)', 'var(--cat-6)', 'var(--cat-7)', 'var(--cat-8)'];
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={() => (abierta = false)} aria-label="Volver">←</button>
      <h1>Informes</h1>
    </header>

    <div class="contenido">
      {#if !vivos.length}
        <div class="vacio">
          <p class="vacio-titulo">Todavía no hay nada que graficar</p>
          <p class="explica">
            Registra unos movimientos o trae tu historia desde Excel, y aquí
            vas a ver en qué se te va la plata.
          </p>
        </div>
      {:else}
        <!-- Selector de mes -->
        <div class="meses">
          {#each meses.slice(0, 12) as m}
            <button class="chip" class:activo={m === mesElegido} onclick={() => (mesElegido = m)}>
              {m.slice(5)}/{m.slice(2, 4)}
            </button>
          {/each}
        </div>

        <!-- Resumen del mes -->
        <section class="tarjeta">
          <h2>{nombreMes(mesElegido)}</h2>
          <div class="tres">
            <div>
              <span class="rotulo">Entró</span>
              <span class="cifra grande entra">{formatearCorto(ingresos)}</span>
            </div>
            <div>
              <span class="rotulo">Salió</span>
              <span class="cifra grande">{formatearCorto(egresos)}</span>
            </div>
            <div>
              <span class="rotulo">Queda</span>
              <span class="cifra grande" class:entra={balance > 0} class:falta={balance < 0}>
                {formatearCorto(balance)}
              </span>
            </div>
          </div>

          {#if tasaAhorro !== null}
            <p class="tasa" class:bien={tasaAhorro >= 20} class:mal={tasaAhorro < 0}>
              {#if tasaAhorro < 0}
                Gastaste <strong>{Math.abs(tasaAhorro).toFixed(0)}% más</strong> de lo que entró.
              {:else if tasaAhorro >= 20}
                Ahorraste el <strong>{tasaAhorro.toFixed(0)}%</strong> de lo que entró. Vas bien.
              {:else}
                Ahorraste el <strong>{tasaAhorro.toFixed(0)}%</strong>. Lo sano es 20% o más.
              {/if}
            </p>
          {/if}
        </section>

        <!-- Gastos por categoría -->
        {#if porCategoria.length}
          <section>
            <h2>En qué se fue</h2>
            <div class="barras">
              {#each porCategoria.slice(0, 10) as c, i}
                {@const pct = mayorCategoria ? (c.monto / mayorCategoria) * 100 : 0}
                {@const pctTotal = egresos ? (c.monto / egresos) * 100 : 0}
                <div class="barra">
                  <div class="barra-cabeza">
                    <span class="barra-nombre">{c.nombre}</span>
                    <span class="cifra barra-valor" aria-hidden="true">{formatear(c.monto)}</span>
                    <span class="solo-lectores">{c.nombre}: {formatearAria(c.monto)}</span>
                  </div>
                  <div class="riel">
                    <!-- Solo se anima el ancho al entrar; después queda quieto
                         para que no distraiga al leer las cifras. -->
                    <div
                      class="relleno"
                      style="width:{pct}%; background:{COLORES[i % COLORES.length]}; animation-delay:{i * 30}ms"
                    ></div>
                  </div>
                  <span class="barra-pct">{pctTotal.toFixed(0)}% de lo que gastaste</span>
                </div>
              {/each}
            </div>
          </section>
        {/if}

        <!-- Comparación de meses -->
        {#if ultimosMeses.length > 1}
          <section>
            <h2>Mes contra mes</h2>
            <div class="columnas">
              {#each ultimosMeses as m}
                <div class="col">
                  <div class="par-barras">
                    <div
                      class="col-barra entrada"
                      style="height:{(m.ingresos / topeMeses) * 100}%"
                      title="Entró {formatear(m.ingresos)}"
                    ></div>
                    <div
                      class="col-barra salida"
                      style="height:{(m.egresos / topeMeses) * 100}%"
                      title="Salió {formatear(m.egresos)}"
                    ></div>
                  </div>
                  <span class="col-etiqueta">{m.etiqueta}</span>
                </div>
              {/each}
            </div>
            <div class="leyenda">
              <span><i class="punto entrada"></i> entró</span>
              <span><i class="punto salida"></i> salió</span>
            </div>
          </section>
        {/if}

        <!-- Regla 50/30/20 -->
        {#if cincuenta.total > 0}
          <section>
            <h2>Necesidades, gustos y deuda</h2>
            <div class="apilada">
              {#if cincuenta.pctNecesidad > 0}<div class="segmento necesidad" style="width:{cincuenta.pctNecesidad}%"></div>{/if}
              {#if cincuenta.pctDeseo > 0}<div class="segmento deseo" style="width:{cincuenta.pctDeseo}%"></div>{/if}
              {#if cincuenta.pctDeuda > 0}<div class="segmento deuda" style="width:{cincuenta.pctDeuda}%"></div>{/if}
              {#if cincuenta.pctSin > 0}<div class="segmento sin" style="width:{cincuenta.pctSin}%"></div>{/if}
            </div>
            <div class="leyenda-503020">
              <span><i class="punto necesidad"></i> Necesidades {cincuenta.pctNecesidad.toFixed(0)}%</span>
              <span><i class="punto deseo"></i> Gustos {cincuenta.pctDeseo.toFixed(0)}%</span>
              {#if cincuenta.pctDeuda > 0}<span><i class="punto deuda"></i> Deuda y ahorro {cincuenta.pctDeuda.toFixed(0)}%</span>{/if}
              {#if cincuenta.pctSin > 0}<span><i class="punto sin"></i> Sin clasificar {cincuenta.pctSin.toFixed(0)}%</span>{/if}
            </div>
            <p class="explica">
              La guía clásica dice 50% necesidades, 30% gustos y 20% ahorro.
              No es ley: sirve para ver si algo se salió de proporción.
            </p>
          </section>
        {/if}

        <!-- Dónde más gastas -->
        {#if topComercios.length}
          <section>
            <h2>Dónde más gastaste</h2>
            <div class="lista-simple">
              {#each topComercios as c}
                <div class="fila-simple">
                  <span>{c.nombre}</span>
                  <span class="cifra">{formatear(c.monto)}</span>
                </div>
              {/each}
            </div>
          </section>
        {/if}

        <!-- El 4x1000 del año -->
        {#if gmfDelAnio > 0}
          <section class="tarjeta destacada">
            <h2>4x1000 en {mesElegido.slice(0, 4)}</h2>
            <p class="cifra enorme">{formatear(gmfDelAnio)}</p>
            <p class="explica">
              Es lo que se ha ido en impuesto a los movimientos. Se muestra
              aparte porque es plata que no compró nada, y viéndola junta
              es más fácil decidir mover menos veces.
            </p>
          </section>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-6);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }

  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  /* ── Meses ── */
  .meses {
    display: flex;
    gap: var(--e-1);
    overflow-x: auto;
    scrollbar-width: none;
  }
  .meses::-webkit-scrollbar { display: none }
  .chip {
    flex: 0 0 auto;
    padding: var(--e-2) var(--e-3);
    border-radius: var(--r-full);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    color: var(--ink-2);
    background: var(--bg-sunken);
    min-height: var(--tap-min);
  }
  .chip.activo { background: var(--acento); color: var(--on-acento) }

  /* ── Tarjeta de resumen ── */
  .tarjeta {
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .tarjeta h2 { text-transform: none; letter-spacing: 0; font-size: var(--t-sm); color: var(--ink-2) }

  .tres { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--e-2); margin-top: var(--e-2) }
  .tres div { display: flex; flex-direction: column; gap: 2px }
  .rotulo { font-size: var(--t-2xs); color: var(--ink-3); text-transform: uppercase; letter-spacing: var(--track-wide) }
  .grande { font-size: var(--t-lg); font-weight: var(--w-bold); white-space: nowrap }
  .entra { color: var(--ingreso) }
  .falta { color: var(--peligro) }

  .tasa {
    font-size: var(--t-sm);
    color: var(--ink-2);
    margin-top: var(--e-3);
    padding-top: var(--e-3);
    border-top: 1px solid var(--border);
    line-height: var(--lh-loose);
  }
  .tasa.bien { color: var(--ingreso) }
  .tasa.mal { color: var(--peligro) }

  .destacada { text-align: center }
  .enorme { font-size: var(--t-3xl); font-weight: var(--w-bold); color: var(--alerta); margin: var(--e-2) 0 }

  /* ── Barras horizontales ── */
  .barras { display: flex; flex-direction: column; gap: var(--e-3) }
  .barra { display: flex; flex-direction: column; gap: 4px }
  .barra-cabeza { display: flex; justify-content: space-between; gap: var(--e-2); align-items: baseline }
  .barra-nombre {
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .barra-valor { font-size: var(--t-sm); font-weight: var(--w-semibold); white-space: nowrap }
  .barra-pct { font-size: var(--t-2xs); color: var(--ink-3) }

  .riel {
    height: 8px;
    background: var(--bg-sunken);
    border-radius: var(--r-full);
    overflow: hidden;
  }
  .relleno {
    height: 100%;
    border-radius: var(--r-full);
    transform-origin: left;
    animation: crecer var(--d-lenta) var(--ease-entrada) backwards;
  }
  @keyframes crecer {
    from { transform: scaleX(0) }
    to   { transform: scaleX(1) }
  }

  /* ── Columnas mes a mes ── */
  .columnas {
    display: flex;
    gap: var(--e-2);
    align-items: flex-end;
    height: 130px;
    padding-top: var(--e-2);
  }
  .col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: var(--e-1); height: 100% }
  .par-barras { flex: 1; display: flex; gap: 3px; align-items: flex-end; width: 100% }
  .col-barra {
    flex: 1;
    min-height: 2px;
    border-radius: var(--r-xs) var(--r-xs) 0 0;
    animation: subir var(--d-lenta) var(--ease-entrada) backwards;
  }
  @keyframes subir { from { transform: scaleY(0) } to { transform: scaleY(1) } }
  .col-barra { transform-origin: bottom }
  .entrada { background: var(--ingreso) }
  .salida { background: var(--ink-3) }
  .col-etiqueta { font-size: var(--t-2xs); color: var(--ink-3) }

  .leyenda, .leyenda-503020 {
    display: flex;
    flex-wrap: wrap;
    gap: var(--e-3);
    font-size: var(--t-xs);
    color: var(--ink-3);
  }
  .punto {
    width: 8px;
    height: 8px;
    border-radius: var(--r-full);
    display: inline-block;
    margin-right: 4px;
  }

  /* ── Barra apilada 50/30/20 ── */
  .apilada {
    display: flex;
    height: 14px;
    border-radius: var(--r-full);
    overflow: hidden;
    background: var(--bg-sunken);
  }
  .segmento { height: 100%; animation: crecer var(--d-lenta) var(--ease-entrada) backwards }
  .necesidad { background: var(--cat-1) }
  .deseo     { background: var(--cat-3) }
  .deuda     { background: var(--peligro) }
  .sin       { background: var(--ink-3) }

  /* ── Lista simple ── */
  .lista-simple { display: flex; flex-direction: column }
  .fila-simple {
    display: flex;
    justify-content: space-between;
    gap: var(--e-3);
    padding: var(--e-2) 0;
    font-size: var(--t-sm);
    border-bottom: 1px solid var(--border);
  }
  .fila-simple:last-child { border-bottom: none }

  .vacio { padding: var(--e-16) 0; text-align: center }
  .vacio-titulo { font-size: var(--t-lg); font-weight: var(--w-semibold); color: var(--ink-2); margin-bottom: var(--e-2) }
</style>
